import { useQueryErrorResetBoundary } from '@tanstack/react-query';
import { type ErrorComponentProps, Link, useRouter } from '@tanstack/react-router';
import { Compass } from 'lucide-react';
import { useEffect, useState } from 'react';
import { EmptyState } from '../guidance/empty-state';
import { ErrorState } from '../guidance/error-state';

/**
 * Any screen that fails to render or load shows this in place of its content; the navigation stays, so the rest
 * of the app is one click away (task 27).
 */
export function RouteError({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const queries = useQueryErrorResetBoundary();
  useEffect(() => {
    console.error('screen error', error);
    queries.reset();
  }, [error, queries]);
  return (
    <ErrorState
      error={error}
      learnMore="when-something-goes-wrong"
      onRetry={() => {
        reset();
        void router.invalidate();
      }}
    >
      <Link to="/" className="text-sm underline underline-offset-2">
        Go to Home
      </Link>
    </ErrorState>
  );
}

/** Unknown address. */
export function NotFound() {
  return (
    <EmptyState icon={Compass} title="Page not found" learnMore="when-something-goes-wrong">
      <p>There’s nothing at this address. The link may be old, or the page has moved.</p>
      <p className="mt-2">
        <Link to="/" className="underline underline-offset-2">
          Go to Home
        </Link>{' '}
        or search with <kbd className="rounded border bg-muted px-1 font-mono text-xs">⌘K</kbd>.
      </p>
    </EmptyState>
  );
}

/** A thin bar while the browser reports no network; queries retry when it returns. */
export function OfflineBanner() {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  if (online) return null;
  return (
    <div role="status" className="mb-4 rounded-md border border-warning bg-warning/15 px-3 py-1.5 text-sm text-warning-foreground">
      You’re offline. Changes can’t be saved until the connection is back; the page picks up where it was.
    </div>
  );
}
